import { Component, EventEmitter, Input, Output } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ProgramsService } from "../../../../core/services/programs.service";
import { LoaderService } from "../../../../core/services/loader.service";
import { AddProgramDto } from "../../../../core/models/program/addProgramDto";
import { DropDownDto } from "../../../../core/models/program/dropdownDto";
import { DrawerModule } from "primeng/drawer";
import { CommonModule } from "@angular/common";
import { SelectModule } from "primeng/select";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { RadioButtonModule } from "primeng/radiobutton";

@UntilDestroy()
@Component({
  selector: "jni-add-program-blade",
  templateUrl: "./add-program-blade.component.html",
  imports: [
    DrawerModule,
    ReactiveFormsModule,
    CommonModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    CheckboxModule,
    RadioButtonModule,
  ],
  standalone: true,
  styleUrls: ["./add-program-blade.component.scss"],
})
export class AddProgramBladeComponent {
  constructor(
    private _programService: ProgramsService,
    private _fb: FormBuilder,
    private _toastrService: JniToastrService,
    private _loaderService: LoaderService
  ) {
    this.initailizeForm();
  }

  programForm!: FormGroup;
  createNewProgram = "Add Program";
  programData = {} as AddProgramDto;
  threePl1!: number;
  platformId!: number;
  facilitatorId!: number;
  onboardedYes = false;
  onboardedNo = false;

  @Input() threePLList: DropDownDto[] = [];
  @Input() platformList: DropDownDto[] = [];
  @Input() facilitatorList: DropDownDto[] = [];
  @Input() display!: boolean;
  // @Input() result$!: Observable<any>; // Replace 'any' with a specific type if possible
  @Output() getProgramData = new EventEmitter<AddProgramDto>();
  @Output() displayChange = new EventEmitter<boolean>();
  @Output() getLoaderStatus = new EventEmitter<boolean>();

  formErrors: Record<string, string> = {
    name: "",
  };

  validationMessages: Record<string, Record<string, string>> = {
    name: {
      required: "Program name is required.",
    },
  };

  logValidationErrors(group: FormGroup = this.programForm): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.logValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (
          abstractControl &&
          !abstractControl.valid &&
          (abstractControl.touched || abstractControl.dirty)
        ) {
          const messages = this.validationMessages[key];
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  initailizeForm() {
    this.programForm = this._fb.group({
      id: [""],
      name: ["", Validators.required],
      brandingConsultantD365: [""],
      office: [""],
      description: [""],
      ecomOnboard: [false],
      inStoreCC: [false],
      usesWebsite: [false],
      clientOwnInv: [false],
      nadelOwnInv: [false],
      intl: [false],
      website1: [""],
      website2: [""],
      dimensionD365: [""],
      uses3PL: [false],
      micro: [false],
      threePL1Id: [""],
      threePLOther: [""],
      avaTaxIntegration: [false],
      webPlatId: [""],
      inStoreCCProvider: [0],
      isApproved: [false],
      isDisabled: [false],
      isClientLock: [false],
    });
  }

  onHide() {
    this.displayChange.emit(false);
  }

  onSubmit() {
    const formValues = this.programForm.getRawValue();
    if (this.programForm.invalid) {
      this._toastrService.error("Error", "Error adding program");
      return;
    }
    this.programFieldMapper(formValues);
    this._loaderService.setLoaderState(true);
    this._programService
      .createProgram(this.programData)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res.data != null) {
            this.programData = res.data;
            this.getProgramData.emit(this.programData);
          } else {
            this._toastrService.error(
              "Error",
              res.message || "Error adding program"
            );
          }
          this.displayChange.emit(false);
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastrService.error(
            "Error",
            res.message || "Error adding program"
          );
        },
      });
  }

  onRadioButtonClick(value: boolean) {
    if (value === true) {
      this.onboardedYes = true;
      this.onboardedNo = false;
      //this.programForm.controls["ecomOnboard"].setValue(true);
    } else {
      this.onboardedYes = false;
      this.onboardedNo = true;
      //this.programForm.controls["ecomOnboard"].setValue(false);
    }
  }

  programFieldMapper(data: AddProgramDto) {
    this.programData.name = data.name;
    this.programData.dimensionD365 = data.dimensionD365;
    this.programData.office = data.office;
    this.programData.description = data.description;
    this.programData.brandingConsultantD365 = data.brandingConsultantD365;
    this.programData.nadelOwnInv = data.nadelOwnInv;
    this.programData.clientOwnInv = data.clientOwnInv;
    this.programData.uses3PL = data.uses3PL;
    this.programData.threePL1Id = data.threePL1Id || 0;
    this.programData.usesWebsite = data.usesWebsite;
    this.programData.webPlatId = data.webPlatId || 0;
    this.programData.threePLOther = data.threePLOther;
    this.programData.inStoreCC = data.inStoreCC;
    this.programData.inStoreCCProvider =
      data.inStoreCCProvider == null ? 0 : (data.inStoreCCProvider ?? 0);
    this.programData.intl = data.intl;
    this.programData.micro = data.micro;
    this.programData.ecomOnboard =
      String(data.ecomOnboard).toLowerCase() === "true";
    this.programData.website1 = data.website1;
    this.programData.website2 = data.website2;
    this.programData.isApproved = data.isApproved;
    this.programData.isDisabled = data.isDisabled;
    this.programData.isClientLock = data.isClientLock;
  }

  patchValuesInNadelOwnedAndThirdPartyfields(
    nadelOwnedInventory: boolean,
    thirdPartyLogistics: boolean
  ) {
    this.programForm.patchValue({
      nadelOwnInv: nadelOwnedInventory,
      uses3PL: thirdPartyLogistics,
    });
  }

  patchValuesInProgramBcfield(brandingConsultantId: string) {
    this.programForm.patchValue({
      brandingConsultantD365: brandingConsultantId,
    });
  }
}
